import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isSelectionReadyForCheckout } from '../services/boothRulesService';
import { getMyLatestApplicationForEvent, getMyLatestApplication, getLatestApplicationForVendorEvent } from '../services/applicationsService';
import { getUserProfile } from '../services/profilesService';
import { listVendorsForUser } from '../services/vendorsService';
import { createPaymentIntent } from '../services/paymentsService';

const normalizeConfiguredValue = (value) => {
  const normalized = String(value || '').trim();
  return normalized.toLowerCase() === 'na' ? '' : normalized;
};
import { createTransaction } from '../services/transactionsService';
import { getEvent } from '../services/eventsService';
import './BoothShoppingCart.css';

const BoothShoppingCart = ({ 
  selectedBooths = [], 
  gridData = [], 
  onRemoveBooth, 
  onCheckout,
  onClearCart,
  rulesConfig,
  show = false,
  onClose,
  getLevelPricing,
  customLevels,
  getLevelColor,
  variant = 'overlay', // 'overlay' | 'embedded'
  onCollapse, // optional: when provided in embedded mode, shows a collapse button that hides the cart
  timeLeftSeconds,
  shoppingCartWaitMinutes,
  hideCheckout = false, // when true (Preview Mode), do not render the checkout button
  eventId = null, // current event context for application approval
  eventName = '', // event name for payment description
  layoutId = null, // layout ID for transaction record
  paymentWaitMinutes // payment completion time limit (required)
}) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [showCheckoutValidation, setShowCheckoutValidation] = useState(false);
  const [expanded, setExpanded] = useState(false); // for embedded variant manual expand/collapse
  const [processingCheckout, setProcessingCheckout] = useState(false);
  const [showPricesModal, setShowPricesModal] = useState(false);
  const [isAdminView, setIsAdminView] = useState(false);

  if (!show && variant !== 'embedded') return null;

  // Helper function to get the effective corner value considering manual overrides
  const getEffectiveCornerValue = (booth) => {
    if (booth.cornerOverride) {
      return booth.manualCorner || booth.corner;
    }
    return booth.corner;
  };

  // Get selected booth details
  const selectedBoothDetails = selectedBooths
    .map(boothId => gridData.find(booth => booth.id === boothId))
    .filter(Boolean)
    .sort((a, b) => a.number - b.number);

  // Calculate booth price using proper pricing system
  const calculateBoothPrice = (booth) => {
    if (!getLevelPricing || booth.status === 'Invisible') {
      return 0;
    }
    
    const effectiveCornerValue = getEffectiveCornerValue(booth);
    const isCorner = effectiveCornerValue === 'Y';
    const price = getLevelPricing(booth.level, isCorner, customLevels);   
    
    return price;
  };

  const total = selectedBoothDetails.reduce((sum, booth) => {
  const price = calculateBoothPrice(booth);
  return sum + (typeof price === 'number' ? price : 0);
  }, 0);
  
  // Check if ready for checkout
  const checkoutValidation = isSelectionReadyForCheckout(selectedBooths, gridData, rulesConfig);
  // Application approval: scope to current event when available.
  // We accept an optional eventId via gridData booth context (passed down from parent BoothSelection via props).
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [appLoading, setAppLoading] = useState(true);
  const [vendorIdLocal, setVendorIdLocal] = useState(null);
  // Derive eventId from first booth's eventId if not passed explicitly via props (parent now passes eventId prop)
  // but we add prop support for clarity.
  const eventIdFromGrid = Array.isArray(gridData) && gridData.length > 0 ? gridData[0].eventId || null : null;
  const effectiveEventId = eventId || (typeof window !== 'undefined' && window.__boothSelectionEventId) || eventIdFromGrid || null;
  
  // Load vendorId for the current user (if any) so vendor-owned applications can be detected
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!currentUser?.uid) return;
      try {
        const vendors = await listVendorsForUser(currentUser.uid);
        if (!cancelled && vendors && vendors.length > 0) {
          setVendorIdLocal(vendors[0].id || null);
        }
      } catch (err) {
        // ignore — vendorIdLocal remains null
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.uid]);

  useEffect(() => {
    let cancelled = false;
    // Don't fetch if we don't have an eventId yet (wait for gridData to load)
    if (!effectiveEventId) {
      setAppLoading(false);
      return;
    }

    (async () => {
      try {
        let latest = null;
        // If the current user has a vendor record, prefer vendor-based application lookup
        if (vendorIdLocal) {
          latest = await getLatestApplicationForVendorEvent(vendorIdLocal, effectiveEventId);
        }
        // Fallback to user-based lookup if vendor-based didn't find anything
        if (!latest) {
          latest = await getMyLatestApplicationForEvent(effectiveEventId);
        }
        if (!cancelled) {
          setApplicationStatus(latest?.status || null);
        }
      } catch (err) {
        console.error('[BoothShoppingCart] Error fetching application', err);
        if (!cancelled) setApplicationStatus(null);
      } finally {
        if (!cancelled) setAppLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [effectiveEventId, vendorIdLocal]); // Depend on vendorIdLocal so vendor users fetch vendor-scoped apps

  // Determine whether current user is Admin or Organizer to show Admin View badge
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!currentUser?.uid) {
        if (!cancelled) setIsAdminView(false);
        return;
      }
      try {
        const profile = await getUserProfile(currentUser.uid);
        if (!cancelled) setIsAdminView(profile && (profile.userType === 'Admin' || profile.userType === 'Organizer'));
      } catch (e) {
        if (!cancelled) setIsAdminView(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.uid]);

  // Normalize approval check (case-insensitive) for UI logic
  const isApproved = (applicationStatus || '').toString().toLowerCase() === 'approved';
  const displayApplicationStatus = applicationStatus ? String(applicationStatus).toString() : 'None Submitted';

  const handleCheckoutClick = async () => {
    if (!checkoutValidation.ready) {
      setShowCheckoutValidation(true);
      return;
    }

    if (!currentUser?.uid) {
      alert('Please log in to proceed with checkout.');
      return;
    }

    const parsedPaymentWait = Number.parseInt(paymentWaitMinutes, 10);
    if (!Number.isFinite(parsedPaymentWait) || parsedPaymentWait <= 0) {
      alert('Checkout is temporarily unavailable: payment timer configuration is missing or invalid.');
      return;
    }

    // Check if cart timer has already expired (race condition check)
    const sessionKey = `cartSession:${currentUser.uid}:${eventId}:${layoutId}`;
    const expiryKey = `cartExpiry:${currentUser.uid}:${eventId}:${layoutId}`;
    try {
      const sessionId = sessionStorage.getItem(sessionKey);
      if (sessionId) {
        const expiryStorageKey = `${expiryKey}:${sessionId}`;
        const storedExpiry = sessionStorage.getItem(expiryStorageKey);
        if (storedExpiry) {
          const expiryTime = parseInt(storedExpiry);
          if (Date.now() >= expiryTime) {
            alert('Your cart time has expired. Please select your booths again.');
            return;
          }
        }
      }
    } catch (e) {
      console.warn('[BoothShoppingCart] Could not check cart expiry', e);
    }

    // Show confirmation dialog before proceeding
    const confirmed = window.confirm(
      'Once you proceed to checkout, you will not be able to return to make changes to your booth selection.\n\n' +
      'Your selected booths will be held during the payment process.\n\n' +
      'Are you ready to proceed to checkout?'
    );
    
    if (!confirmed) {
      return; // User cancelled
    }

    setProcessingCheckout(true);

    try {
      // Get user profile and vendor info
      const profile = await getUserProfile(currentUser.uid);
      const vendors = await listVendorsForUser(currentUser.uid);
      const vendor = vendors && vendors.length > 0 ? vendors[0] : null;

      // Prepare booth data
      const boothNumbers = selectedBoothDetails.map(b => b.number).join(', ');
      const boothCount = selectedBoothDetails.length;

      // Build payment metadata
      const paymentSessionKey = `cartSession:${currentUser.uid}:${eventId}:${layoutId}`;
      const sessionId = sessionStorage.getItem(paymentSessionKey) || '';
      const paymentMetadata = {
        source: 'ICNACON Events App',
        payeeName: profile ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim() : '',
        payeeEmail: profile?.email || currentUser.email || '',
        payeePhone: profile?.phone || '',
        vendorId: vendor?.id || profile?.vendorId || '', // Add vendorId for reservation ownership
        vendorName: vendor?.name || '',
        vendorNumber: vendor?.vendorNumber || '',
        eventName: eventName || 'ICNA Event',
        boothNumbers: boothNumbers,
        boothIds: selectedBoothDetails.map((b) => b?.id).filter(Boolean).join(','),
        boothCount: boothCount.toString(),
        eventId: eventId || '',
        layoutId: layoutId || '',
        createdByProfileId: currentUser.uid,
        sessionId,
        purchaseType: 'booths',
      };

      // Create dynamic description with environment prefix
      const env = import.meta.env.VITE_APP_ENV || 'dev';
      let envPrefix = '';
      if (env === 'dev') {
        envPrefix = 'DEV: ';
      } else if (env === 'test') {
        envPrefix = 'TST: ';
      }
      // Production has no prefix
      
      const description = `${envPrefix}${eventName || 'Event'}-BAZAAR-${vendor?.name || 'Vendor'}-Booth-Count ${boothCount}`;

      // Create payment intent
      // `calculateBoothPrice` returns prices in cents and `total` is the sum in cents.
      // Use `total` directly as `amountInCents` to avoid double-conversion.
      const amountInCents = total;
      
      // Fetch event to get paymentConfigId
      let paymentMethodConfiguration = '';
      try {
        if (effectiveEventId) {
          const event = await getEvent(effectiveEventId);
          paymentMethodConfiguration = normalizeConfiguredValue(event?.bazaarPaymentInfo?.paymentConfigId);
        }
      } catch (err) {
        console.warn('[BoothShoppingCart] Failed to fetch event for paymentConfigId:', err);
      }
      
      const result = await createPaymentIntent({
        amount: amountInCents,
        currency: 'usd',
        description: description,
        metadata: paymentMetadata,
        eventId: effectiveEventId,
        payment_method_types: ['card', 'amazon_pay'],
        payment_method_configuration: paymentMethodConfiguration,
      });

      // Create initial transaction record with 'incomplete' status
      // This will be updated to 'succeeded' or 'failed' when payment completes
      try {
        const _resolvedEventId = eventId || (paymentMetadata && paymentMetadata.eventId) || '';
        if (!_resolvedEventId) {
          try {
            // One-time debug: log when eventId could not be resolved for this createTransaction
            // eslint-disable-next-line no-console
            console.warn('[BoothShoppingCart] createTransaction: no eventId resolved for paymentIntentId', result.paymentIntentId, 'metadata:', paymentMetadata);
          } catch (e) {}
        }

        await createTransaction({
          paymentIntentId: result.paymentIntentId,
          profileId: currentUser.uid,
          eventId: _resolvedEventId,
          layoutId: layoutId || null,
          amount: amountInCents,
          currency: 'usd',
          status: 'incomplete',
          metadata: paymentMetadata,
          booths: selectedBoothDetails.map(b => ({
            id: b.id || '',
            number: b.number || b.boothId || '',
            level: b.level || '',
            corner: b.corner || '',
            row: b.row || 0,
            col: b.col || 0,
            price: calculateBoothPrice(b), // Always use calculated price in cents
          })),
        });
      } catch (txError) {
        console.error('[BoothShoppingCart] Failed to create initial transaction record:', txError);
        // Don't block checkout if transaction logging fails
      }

      // Mark that checkout has started - this will stop cart timer and clear cart items
      const checkoutKey = `checkoutStarted:${currentUser.uid}:${eventId}:${layoutId}`;
      const sessionKey = `cartSession:${currentUser.uid}:${eventId}:${layoutId}`;
      const expiryKey = `cartExpiry:${currentUser.uid}:${eventId}:${layoutId}`;

      try {
        // Set checkout flag
        sessionStorage.setItem(checkoutKey, Date.now().toString());

        // Clear cart timer by removing expiry
        const sessionId = sessionStorage.getItem(sessionKey);
        if (sessionId) {
          const expiryStorageKey = `${expiryKey}:${sessionId}`;
          sessionStorage.removeItem(expiryStorageKey);
        }
      } catch (e) {
        // Could not set checkoutStarted flag
      }

      // Navigate to checkout page with payment intent and order data
      navigate('/checkout', {
        state: {
          orderData: {
            amount: amountInCents,
            currency: 'usd',
            description: description,
            eventName: eventName || 'ICNA Event',
            booths: selectedBoothDetails.map(b => ({
              ...b,
              price: calculateBoothPrice(b) // Use calculated price in cents
            })),
            metadata: paymentMetadata,
          },
          paymentIntentId: result.paymentIntentId,
          clientSecret: result.clientSecret,
          // Pass payment timeout configuration and start time
          paymentWaitMinutes: parsedPaymentWait,
          paymentStartTime: Date.now(), // Start timer NOW when user clicks checkout
          eventId: eventId,
          layoutId: layoutId,
        }
      });

      // NOTE: Do NOT call onCheckout here - it should only be called after successful payment
      // The PaymentSuccess component will handle marking booths as Reserved

    } catch (error) {
      console.error('Checkout error:', error);
      alert(`Failed to initiate checkout: ${error.message || 'Unknown error'}`);
    } finally {
      setProcessingCheckout(false);
    }
  };

  const formatCurrency = (amount) => {
    // Treat amount as cents
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format((typeof amount === 'number' ? amount : 0) / 100);
  };

  // Dynamic list height rules for embedded variant:
  // - Show at least 2 rows
  // - Grow up to 6 rows as items increase
  // - Beyond 6 rows, enable scroll
  const cartRef = useRef(null);
  const [rowHeight, setRowHeight] = useState(48); // default until CSS var is read
  const rowsToShow = variant === 'embedded'
    ? Math.min(Math.max(selectedBoothDetails.length, 2), 6)
    : null;

  // Read CSS variable --cart-row-height from the embedded cart element so JS and CSS stay in sync
  useEffect(() => {
    if (cartRef.current) {
      try {
        const cs = getComputedStyle(cartRef.current).getPropertyValue('--cart-row-height');
        if (cs) {
          const parsed = parseFloat(cs);
          if (!isNaN(parsed)) setRowHeight(parsed);
        }
      } catch (e) {
        // ignore
      }
    }
  }, []);

  const listStyle = variant === 'embedded'
    ? (expanded
        ? { maxHeight: '60vh', overflowY: 'auto' }
        : { maxHeight: `${rowsToShow * rowHeight}px`, overflowY: selectedBoothDetails.length > rowsToShow ? 'auto' : 'hidden' })
    : undefined;

    const CartInner = (
      <div ref={cartRef} className={`booth-shopping-cart ${variant === 'embedded' ? 'embedded' : ''} ${variant === 'embedded' && expanded ? 'expanded' : ''}`}>
        <div className="cart-header">
          <h3>
            <span className="cart-icon">🛒</span>
            Booth Selection Cart
              <button
                type="button"
                className="price-grid-btn"
                onClick={() => setShowPricesModal(true)}
                title="Show booth prices by level"
              >
                View Booth Prices
              </button>
              {isAdminView && (
                <span className="admin-view-badge" title="Admin / Organizer view">
                  <span className="badge-icon">⚙️</span>
                  Admin View
                </span>
              )}
          </h3>
          {/* Timer removed from cart header per request; timer is shown in page header only */}
          {variant === 'overlay' && (
            <button className="close-cart-btn" onClick={onClose}>
              ×
            </button>
          )}
          {variant === 'embedded' && (
            <div className="cart-header-actions">
              <button 
                className="expand-cart-btn" 
                onClick={() => setExpanded(prev => !prev)}
                title={expanded ? 'Collapse Height' : 'Expand Height'}
                aria-pressed={expanded}
              >
                {expanded ? '▴ Collapse' : '▾ Expand'}
              </button>
              {typeof onCollapse === 'function' && (
                <button 
                  className="collapse-cart-btn" 
                  onClick={onCollapse}
                  title="Hide Cart"
                >
                  Hide
                </button>
              )}
            </div>
          )}
        </div>

        <div className="cart-content">
          {selectedBoothDetails.length === 0 ? (
            <div className="empty-cart">
              <div className="empty-cart-icon">📋</div>
              <h4>No booths selected</h4>
              <p>Select booths from the grid to add them to your cart</p>
            </div>
          ) : (
            <>
              {/* Cart Items */}
              <div className="cart-items">
                <div className="cart-items-header">
                  <h4>Selected Booths ({selectedBoothDetails.length})</h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div className="cart-total"><strong className="cart-total-label">Total:</strong> <span className="cart-total-amount">{formatCurrency(total)}</span></div>
                    <button 
                      className="clear-cart-btn"
                      onClick={onClearCart}
                      title="Clear all selections"
                    >
                      Clear All
                    </button>
                  </div>
                </div>

                <div className="cart-items-list" style={listStyle}>
                  {selectedBoothDetails.map(booth => (
                    <div key={booth.id} className="cart-item">
                      <div className="booth-info">
                        <div className="booth-number">#{booth.number}</div>
                        <div className="booth-details">
                          <div className="booth-meta">
                            <span className="booth-location">Row {booth.row}, Col {booth.col}</span>
                            <span 
                              className="level-badge-uniform"
                              style={{ 
                                backgroundColor: getLevelColor ? getLevelColor(booth.level, customLevels) : '#6c757d',
                                color: 'white !important',
                                display: 'inline-block',
                                background: getLevelColor ? getLevelColor(booth.level, customLevels) : '#6c757d'
                              }}
                            >
                              {booth.level || 'N/A'}
                            </span>
                            <span className={`position-badge ${getEffectiveCornerValue(booth) === 'Y' ? 'corner' : 'inline'}`}>
                              {getEffectiveCornerValue(booth) === 'Y' ? 'Corner' : 'Inline'}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="booth-price">
                      {formatCurrency(calculateBoothPrice(booth))}
                      </div>
                      
                      <button 
                        className="remove-booth-btn"
                        onClick={() => onRemoveBooth(booth.id)}
                        title="Remove from cart"
                        aria-label={`Remove booth ${booth.number}`}
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Cart Summary removed: total moved into header to save space */}

              {/* Checkout Validation */}
              {!checkoutValidation.ready && (
                <div className="checkout-validation">
                  <div className="validation-warnings">
                    <h5>⚠️ Checkout Requirements</h5>
                    {checkoutValidation.violations.map((violation, index) => (
                      <div key={index} className="validation-error">
                        {violation.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {checkoutValidation.warnings.length > 0 && (
                <div className="checkout-warnings">
                  <h5>💡 Recommendations</h5>
                  {checkoutValidation.warnings.map((warning, index) => (
                    <div key={index} className="validation-warning">
                      {warning.message}
                    </div>
                  ))}
                </div>
              )}

              {/* Action Buttons */}
              <div className="cart-actions">
                {variant === 'overlay' && (
                  <button 
                    className="continue-shopping-btn"
                    onClick={onClose}
                  >
                    Continue Shopping
                  </button>
                )}
                {!hideCheckout && (
                  <button 
                    className={`checkout-btn ${checkoutValidation.ready ? 'ready' : 'blocked'}`}
                    onClick={handleCheckoutClick}
                    disabled={
                      selectedBoothDetails.length === 0 || 
                      processingCheckout || 
                        !isApproved ||
                      (timeLeftSeconds !== null && timeLeftSeconds <= 2) // Disable if timer expired or <2 seconds left
                    }
                  >
                    {processingCheckout ? (
                      'Processing...'
                    ) : timeLeftSeconds !== null && timeLeftSeconds <= 2 ? (
                      'Cart Time Expired'
                    ) : checkoutValidation.ready ? (
                      `Proceed to Checkout (${formatCurrency(total)})`
                    ) : (
                      'Resolve Requirements First'
                    )}
                  </button>
                )}
                {!isApproved && !appLoading && !hideCheckout && (
                  <div className="checkout-disabled-note" style={{ marginLeft: 'auto', color: '#b00', fontSize: 13 }}>
                    Application not approved yet; checkout locked.
                  </div>
                )}
                {hideCheckout && (
                  <div className="checkout-disabled-note" style={{ marginLeft: 'auto', color: '#888', fontSize: 13 }}>
                    Preview mode: checkout is disabled
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Checkout Validation Modal */}
        {showCheckoutValidation && (
          <div className="validation-modal-overlay" onClick={() => setShowCheckoutValidation(false)}>
            <div className="validation-modal" onClick={e => e.stopPropagation()}>
              <div className="validation-modal-header">
                <h4>Checkout Requirements</h4>
                <button onClick={() => setShowCheckoutValidation(false)}>×</button>
              </div>
              <div className="validation-modal-content">
                <p>Please resolve the following issues before proceeding to checkout:</p>
                <ul>
                  {checkoutValidation.violations.map((violation, index) => (
                    <li key={index}>{violation.message}</li>
                  ))}
                </ul>
                {!isApproved && !appLoading && (
                  <div style={{marginTop:10, color:'#b00'}}>
                    Booth application status: <strong>{displayApplicationStatus}</strong>. You can apply first.
                  </div>
                )}
              </div>
              <div className="validation-modal-actions">
                <button onClick={() => setShowCheckoutValidation(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Prices Modal */}
        {showPricesModal && (
          <div className="validation-modal-overlay" onClick={() => setShowPricesModal(false)}>
            <div className="validation-modal" onClick={e => e.stopPropagation()} style={{ width: '700px', maxWidth: '95%' }}>
              <div className="validation-modal-header">
                <h4 style={{ margin: 0 }}>Booth Prices</h4>
                <button onClick={() => setShowPricesModal(false)}>×</button>
              </div>
              <div className="validation-modal-content">
                {customLevels && Object.keys(customLevels).length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '8px', fontWeight: 700, verticalAlign: 'middle' }}>Level</th>
                        <th style={{ textAlign: 'right', padding: '8px', fontWeight: 700, verticalAlign: 'middle' }}>Inline Price</th>
                        <th style={{ textAlign: 'right', padding: '8px', fontWeight: 700, verticalAlign: 'middle' }}>Corner Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const entries = Object.keys(customLevels || {})
                          .filter(k => {
                            if (!k) return false;
                            if (String(k).toLowerCase() === 'invisible') return false;
                            const name = (customLevels[k] && customLevels[k].name) ? String(customLevels[k].name).toLowerCase() : '';
                            if (name === 'invisible') return false;
                            return true;
                          })
                          .map(key => {
                            const lvl = customLevels[key] || {};
                            const inlineP = Number(lvl.inlinePrice || lvl.price || 0) || 0;
                            const cornerP = Number(lvl.cornerPrice || 0) || 0;
                            return { key, lvl, inlineP, cornerP, maxP: Math.max(inlineP, cornerP) };
                          })
                          .sort((a, b) => {
                            // Sort by most expensive first (descending by max price), then by name
                            if (b.maxP !== a.maxP) return b.maxP - a.maxP;
                            const na = (a.lvl.name || a.key || '').toString();
                            const nb = (b.lvl.name || b.key || '').toString();
                            return na.localeCompare(nb);
                          });

                        return entries.map(({ key, lvl, inlineP, cornerP }) => (
                          <tr key={key}>
                            <td style={{ padding: '8px', borderTop: '1px solid #eee', verticalAlign: 'middle', textAlign: 'left' }}>{lvl.name || key}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderTop: '1px solid #eee', verticalAlign: 'middle' }}>{inlineP ? formatCurrency(inlineP) : 'N/A'}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderTop: '1px solid #eee', verticalAlign: 'middle' }}>{cornerP ? formatCurrency(cornerP) : 'N/A'}</td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                ) : (
                  <div>No pricing available for this layout.</div>
                )}
              </div>
              <div className="validation-modal-actions">
                <button onClick={() => setShowPricesModal(false)}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
  );

  if (variant === 'embedded') {
    return (
      <div className="booth-shopping-cart-embedded-container">
        {CartInner}
      </div>
    );
  }

  return (
    <div className="booth-shopping-cart-overlay">
      {CartInner}
    </div>
  );
};

export default BoothShoppingCart;